import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'blueprint_recent_property_ids';
export const MAX_RECENT_PROPERTIES = 8;

// ── Module-level store ────────────────────────────────────────────────────────
// A single in-memory store shared across all hook instances so that a push in
// PropertiesProvider is immediately visible in PropertySelector without a reload.

type Listener = () => void;

let store: string[] | null = null;
const listeners = new Set<Listener>();

const readFromStorage = (): string[] => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Sanitize: keep only strings, deduplicate, cap at MAX_RECENT_PROPERTIES
    const seen = new Set<string>();
    const sanitized: string[] = [];
    for (const item of parsed) {
      if (typeof item === 'string' && !seen.has(item)) {
        seen.add(item);
        sanitized.push(item);
        if (sanitized.length === MAX_RECENT_PROPERTIES) break;
      }
    }
    return sanitized;
  } catch {
    return [];
  }
};

const writeToStorage = (ids: string[]) => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    }
  } catch {
    // ignore storage errors (e.g. private browsing quota)
  }
};

const getStore = (): string[] => {
  if (store === null) {
    store = readFromStorage();
  }
  return store;
};

const setStore = (updater: (prev: string[]) => string[]) => {
  const next = updater(getStore());
  store = next;
  writeToStorage(next);
  listeners.forEach((l) => l());
};

const subscribe = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// ── Test helper ──────────────────────────────────────────────────────────────
// Resets module-level state so unit tests can start from a clean slate.
export const _resetStoreForTesting = () => {
  store = null;
  listeners.clear();
};

// ── Hook ─────────────────────────────────────────────────────────────────────

export const useRecentPropertyIds = () => {
  const recentIds = useSyncExternalStore(subscribe, getStore);

  const pushRecentId = useCallback((id: string) => {
    setStore((prev) => [id, ...prev.filter((i) => i !== id)].slice(0, MAX_RECENT_PROPERTIES));
  }, []);

  return { recentIds, pushRecentId };
};
